import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const SETTLE_DELAY = 150;

/**
 * Keeps the reviews rail full on both sides of the centered card.
 *
 * The section renders the set of reviews three times. This component opens
 * the rail on the middle copy and, whenever scrolling settles on an outer
 * copy, shifts the rail by one whole set. The copies are identical, so the
 * shift is invisible and cards always run off both edges of the page.
 *
 * The arrows are handled here rather than by the slideshow: its bookkeeping
 * treats the first visible slide as the current one, which only holds for a
 * rail whose snapped slide sits at the left edge, not in the middle.
 */
class ReviewsCarouselComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #settleTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    clearTimeout(this.#settleTimer);
  }

  next() {
    this.#step(1);
  }

  previous() {
    this.#step(-1);
  }

  get #setSize() {
    return parseInt(this.getAttribute('set-size') ?? '0', 10);
  }

  get #looped() {
    return parseInt(this.getAttribute('copies') ?? '1', 10) >= 3 && this.#setSize > 0;
  }

  get #rail() {
    return this.querySelector('slideshow-slides');
  }

  /** Slides that take up room in the rail; parked video slots render hidden. */
  get #slides() {
    return [...this.querySelectorAll('slideshow-slide')].filter(
      (slide) => slide instanceof HTMLElement && slide.offsetWidth > 0
    );
  }

  /** Distance between one card and the next. */
  get #stepSize() {
    const [first, second] = this.#slides;
    return first instanceof HTMLElement && second instanceof HTMLElement ? second.offsetLeft - first.offsetLeft : 0;
  }

  /** Width of one whole copy of the set. */
  get #setWidth() {
    return this.#stepSize * this.#setSize;
  }

  async #start() {
    const rail = this.#rail;
    if (!rail || !this.#looped) return;

    await customElements.whenDefined('slideshow-component');

    // Let the slideshow finish its own first-frame setup before moving the rail.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    rail.scrollTo({ left: this.#setWidth, behavior: 'instant' });

    rail.addEventListener('scroll', this.#handleScroll, {
      passive: true,
      signal: this.#abortController?.signal,
    });
  }

  /**
   * @param {1 | -1} direction
   */
  #step(direction) {
    const rail = this.#rail;
    const stepSize = this.#stepSize;
    if (!rail || !stepSize) return;

    // Aim at a whole card from wherever the rail is, so fast clicks don't drift.
    const index = Math.round(rail.scrollLeft / stepSize) + direction;
    rail.scrollTo({ left: index * stepSize, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  #handleScroll = () => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#recenter, SETTLE_DELAY);
  };

  #recenter = () => {
    const rail = this.#rail;
    const setWidth = this.#setWidth;
    const stepSize = this.#stepSize;
    if (!rail || !setWidth) return;

    const index = Math.round(rail.scrollLeft / stepSize);

    if (index < this.#setSize) {
      rail.scrollTo({ left: rail.scrollLeft + setWidth, behavior: 'instant' });
    } else if (index >= this.#setSize * 2) {
      rail.scrollTo({ left: rail.scrollLeft - setWidth, behavior: 'instant' });
    }
  };
}

if (!customElements.get('reviews-carousel-component')) {
  customElements.define('reviews-carousel-component', ReviewsCarouselComponent);
}
